import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Bot, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import Typewriter, { WORDS as FALLBACK_WORDS } from "@/components/common/Typewriter";
import { readCache, fetchWithCache } from "@/lib/swrCache";

const HERO_VIDEO = "/hero.mp4";

// Fallback taglines — used when the CMS hasn't set a `tagline` for a word.
const FALLBACK_TAGLINES = {
  "Acadmax — ERP":
    "Comprehensive campus management ERP for schools, colleges and universities.",
  "Unicrm.ai":
    "State-of-the-art multi-vendor SaaS product for educational consultancy business.",
  "TeachingSoftware.ai":
    "AI-powered classroom platform giving every teacher an adaptive co-pilot.",
  "HelloSir.ai":
    "Voice and chat AI assistant that answers, qualifies and books — 24/7.",
  "Studyportal.ai":
    "Discovery and application engine for global education seekers.",
  "Visainterviews.ai":
    "Interview simulation and coaching AI for visa applicants across every embassy.",
};

const GENERIC_TAGLINE =
  "End-to-end AI automation across Sales, Support, Finance and Operations — engineered by CloudLab Technologies.";

export default function Hero() {
  const videoRef = useRef(null);
  // Hydrate synchronously from sessionStorage so the rotator paints immediately
  // with the last-known words on return visits, then revalidates in background.
  const [heroWords, setHeroWords] = useState(() => readCache("/hero-words")?.data || null);
  const [wordIdx, setWordIdx] = useState(0);

  useEffect(() => {
    fetchWithCache("/hero-words", (fresh) => setHeroWords(fresh || [])).catch(() => {});
  }, []);

  // Derive `words` list + tagline lookup from the API response (fallback to hard-coded defaults)
  const { words, taglineMap } = useMemo(() => {
    if (!heroWords || heroWords.length === 0) {
      return { words: FALLBACK_WORDS, taglineMap: FALLBACK_TAGLINES };
    }
    const w = heroWords.map((h) => h.word);
    const m = {};
    heroWords.forEach((h) => { m[h.word] = h.tagline || FALLBACK_TAGLINES[h.word] || GENERIC_TAGLINE; });
    return { words: w, taglineMap: m };
  }, [heroWords]);

  const currentWord = words[wordIdx] || words[0];
  const currentTagline = taglineMap[currentWord] || GENERIC_TAGLINE;

  const handleWordChange = useCallback((i) => setWordIdx(i), []);
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = true;
    v.load();
    const tryPlay = () => v.play().catch(() => {});
    tryPlay();
    v.addEventListener("loadeddata", tryPlay);
    return () => v.removeEventListener("loadeddata", tryPlay);
  }, []);

  // Seamless loop: seek back to a tiny offset shortly before the true end so the
  // browser never pauses on the last frame — eliminates the "jerk" at loop point.
  const handleTimeUpdate = (e) => {
    const v = e.currentTarget;
    if (v.duration && v.currentTime >= v.duration - 0.15) {
      v.currentTime = 0;
    }
  };

  return (
    <section
      className="relative isolate overflow-hidden bg-slate-950"
      data-testid="hero-section"
      style={{ minHeight: "82vh" }}
    >
      {/* Background video — cropped to remove the black bars baked into the original file */}
      <video
        ref={videoRef}
        className="absolute inset-0 w-full h-full object-cover"
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
        aria-hidden="true"
        data-testid="hero-video"
        onTimeUpdate={handleTimeUpdate}
      >
        <source src={HERO_VIDEO} type="video/mp4" />
      </video>

      {/* Uniform readability wash — subtle enough that the video remains fully visible edge-to-edge */}
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "linear-gradient(180deg, rgba(3, 7, 18, 0.45) 0%, rgba(3, 7, 18, 0.35) 50%, rgba(3, 7, 18, 0.55) 100%)",
        }}
      />

      {/* Soft top-to-bottom gradient for extra polish and to blend into the section below */}
      <div
        aria-hidden="true"
        className="absolute inset-x-0 bottom-0 h-40 pointer-events-none"
        style={{
          background: "linear-gradient(180deg, transparent 0%, hsl(var(--background)) 100%)",
        }}
      />

      {/* Subtle grain to reduce banding on the video */}
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none opacity-[0.06] mix-blend-overlay"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='140' height='140'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.9'/></svg>\")",
        }}
      />

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 pt-32 pb-20 lg:pt-36 lg:pb-24 w-full">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="inline-flex items-center gap-2 mb-6 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur-sm border border-white/15"
          data-testid="hero-badge"
        >
          <Sparkles className="w-3.5 h-3.5 text-sky-300" strokeWidth={2.5} />
          <span className="text-xs sm:text-sm font-medium text-white/90 tracking-tight">
            UK &amp; Pakistan Teams · Since 2012 · 200+ Projects · 500+ Clients
          </span>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.05 }}
          className="font-jakarta font-bold tracking-tight leading-[1.05] text-3xl sm:text-4xl md:text-5xl lg:text-[56px] max-w-5xl text-white"
          data-testid="hero-title"
          style={{ textShadow: "0 2px 24px rgba(0,0,0,0.4)" }}
        >
          Custom Software, AI &amp; Mobile App Development
          <br className="hidden sm:block" />
          <span className="text-sky-300"> for Growing Businesses.</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="mt-6 max-w-2xl text-base sm:text-lg leading-relaxed text-white/85"
          data-testid="hero-lede"
          style={{ textShadow: "0 1px 12px rgba(0,0,0,0.35)" }}
        >
          CloudLab Technologies designs and develops custom CRM systems, AI-powered business applications, mobile apps and enterprise software for organisations across the UK, Pakistan and international markets.
        </motion.p>

        {/* Signature typewriter — now a supporting animated line under the commercial H1 */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.25 }}
          className="mt-6 max-w-2xl"
          data-testid="hero-typewriter-line"
        >
          <div className="text-xs sm:text-sm font-mono uppercase tracking-[0.2em] text-white/60">Recent products we've built</div>
          <div className="mt-2 text-xl sm:text-2xl font-semibold text-white/90" style={{ textShadow: "0 1px 12px rgba(0,0,0,0.35)" }}>
            <Typewriter words={words} onWordChange={handleWordChange} />
          </div>
          <AnimatePresence mode="wait">
            <motion.p
              key={currentWord}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.3 }}
              className="mt-2 text-sm text-white/70 min-h-[1.5rem]"
              data-testid="hero-subtitle"
            >
              {currentTagline}
            </motion.p>
          </AnimatePresence>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.35 }}
          className="mt-10 flex flex-wrap items-center gap-3"
        >
          <button
            data-testid="hero-cta-estimate"
            onClick={() => window.dispatchEvent(new CustomEvent("open-estimate-wizard", { detail: { source: "hero_primary" } }))}
            className="group rounded-full pl-6 pr-3 py-3 bg-primary text-primary-foreground text-sm font-semibold inline-flex items-center gap-3 hover:brightness-110 transition"
          >
            <span>Get a Free Project Estimate</span>
            <span className="w-9 h-9 rounded-full bg-slate-900/25 flex items-center justify-center transition-transform duration-300 group-hover:translate-x-0.5">
              <ArrowRight className="w-4 h-4" />
            </span>
          </button>
          <button
            data-testid="hero-cta-consultation"
            onClick={() => window.dispatchEvent(new CustomEvent("open-ai-agent", { detail: { source: "hero_secondary" } }))}
            className="group rounded-full pl-6 pr-3 py-3 bg-white/10 border border-white/25 text-white text-sm font-medium inline-flex items-center gap-3 hover:bg-white/15 transition backdrop-blur-sm"
          >
            <span className="w-6 h-6 rounded-full bg-primary/25 text-primary flex items-center justify-center">
              <Bot className="w-3.5 h-3.5" />
            </span>
            <span>Book a 15-Minute Consultation</span>
          </button>
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.7, delay: 0.45 }}
          className="mt-6 text-xs text-white/60"
          data-testid="hero-assessment-time"
        >
          Typical initial project assessment: within 24 hours
        </motion.p>
      </div>
    </section>
  );
}
